/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { TrendingUp, Target, Award, BookOpen, BarChart3 } from 'lucide-react';

interface FretboardProgressProps {
  currentKey: string;
  currentScale: string;
}

interface TestResult {
  questionsAnswered: number;
  correctAnswers: number;
  timeSpent: number;
  accuracy: number;
  completedAt: Date;
}

interface ProgressData {
  totalTests: number;
  totalQuestions: number;
  totalCorrect: number;
  averageAccuracy: number;
  bestAccuracy: number;
  recentTests: TestResult[];
  improvementTrend: number;
}

const FretboardProgress: React.FC<FretboardProgressProps> = ({
  currentKey,
  currentScale
}) => {
  const [progressData, setProgressData] = useState<ProgressData>({
    totalTests: 0,
    totalQuestions: 0,
    totalCorrect: 0,
    averageAccuracy: 0,
    bestAccuracy: 0,
    recentTests: [],
    improvementTrend: 0
  });

  // Load progress data from localStorage
  useEffect(() => {
    const testResults = JSON.parse(localStorage.getItem('fretboardTestResults') || '[]') as TestResult[];
    
    if (testResults.length === 0) {
      return; // Keep default empty state
    }

    // Convert string dates back to Date objects
    const results = testResults.map(result => ({
      ...result,
      completedAt: new Date(result.completedAt)
    }));

    const totalQuestions = results.reduce((sum, result) => sum + result.questionsAnswered, 0);
    const totalCorrect = results.reduce((sum, result) => sum + result.correctAnswers, 0);
    const averageAccuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
    const bestAccuracy = results.length > 0 ? Math.max(...results.map(r => r.accuracy)) : 0;
    
    // Get recent tests (last 10)
    const recentTests = results
      .sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime())
      .slice(0, 10);

    // Calculate improvement trend (compare first half vs second half of recent tests)
    let improvementTrend = 0;
    if (recentTests.length >= 4) {
      const halfPoint = Math.floor(recentTests.length / 2);
      const recentHalf = recentTests.slice(0, halfPoint);
      const olderHalf = recentTests.slice(halfPoint);
      
      const recentAvg = recentHalf.reduce((sum, r) => sum + r.accuracy, 0) / recentHalf.length;
      const olderAvg = olderHalf.reduce((sum, r) => sum + r.accuracy, 0) / olderHalf.length;
      
      improvementTrend = recentAvg - olderAvg;
    }

    setProgressData({
      totalTests: results.length,
      totalQuestions,
      totalCorrect,
      averageAccuracy,
      bestAccuracy,
      recentTests,
      improvementTrend
    });
  }, []);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show welcome message if no data
  if (progressData.totalTests === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Fretboard Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Card className="p-8 bg-blue-50 border-blue-200 text-center">
            <BarChart3 className="h-16 w-16 mx-auto text-blue-600 mb-4" />
            <h3 className="font-semibold text-blue-900 mb-2">No Test Data Yet</h3>
            <p className="text-sm text-blue-800 mb-4">
              Take your first test to start tracking your progress and see detailed statistics.
            </p>
            <p className="text-xs text-blue-700">
              Switch to the <strong>Test</strong> tab to begin your fretboard knowledge assessment.
            </p>
          </Card>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Fretboard Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{progressData.averageAccuracy}%</div>
              <div className="text-sm text-muted-foreground">Average Accuracy</div>
              <div className="text-xs text-muted-foreground mt-1">
                {progressData.totalCorrect}/{progressData.totalQuestions} questions
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{progressData.bestAccuracy}%</div>
              <div className="text-sm text-muted-foreground">Best Score</div>
              <div className="text-xs text-muted-foreground mt-1">
                {progressData.totalTests} tests taken
              </div>
            </div>
          </Card>
        </div>

        {/* Performance Trend */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-4 w-4" />
            <h4 className="font-semibold">Performance Trend</h4>
          </div>
          
          <div className="text-center p-4">
            {progressData.improvementTrend > 5 && (
              <div className="text-green-600">
                <TrendingUp className="h-8 w-8 mx-auto mb-2" />
                <div className="font-semibold">Improving!</div>
                <div className="text-sm">+{Math.round(progressData.improvementTrend)}% recent progress</div>
              </div>
            )}
            {Math.abs(progressData.improvementTrend) <= 5 && (
              <div className="text-blue-600">
                <Target className="h-8 w-8 mx-auto mb-2" />
                <div className="font-semibold">Steady Progress</div>
                <div className="text-sm">Consistent performance</div>
              </div>
            )}
            {progressData.improvementTrend < -5 && (
              <div className="text-amber-600">
                <BookOpen className="h-8 w-8 mx-auto mb-2" />
                <div className="font-semibold">Keep Practicing</div>
                <div className="text-sm">Focus on areas that need work</div>
              </div>
            )}
          </div>
        </Card>

        {/* Recent Tests */}
        <Card className="p-4">
          <h4 className="font-semibold mb-3">Recent Tests</h4>
          {progressData.recentTests.length > 0 ? (
            <div className="space-y-2">
              {progressData.recentTests.map((test, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="text-sm">
                    <div className="font-medium">{formatDate(test.completedAt)}</div>
                    <div className="text-xs text-muted-foreground">
                      {test.questionsAnswered} questions • {formatTime(test.timeSpent)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16">
                      <Progress value={test.accuracy} className="h-2" />
                    </div>
                    <Badge 
                      variant={test.accuracy >= 80 ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {test.accuracy}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No test history yet. Take a test to see your progress here.
            </p>
          )}
        </Card>

        {/* Learning Goals */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Award className="h-4 w-4" />
            <h4 className="font-semibold">Goals</h4>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Take {progressData.totalTests < 5 ? 'your first 5' : 'more'} tests</span>
              <Badge variant="outline" className="text-xs">
                {progressData.totalTests < 5 ? `${progressData.totalTests}/5` : '✓ Complete'}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Achieve 80% average accuracy</span>
              <Badge variant="outline" className="text-xs">
                {progressData.averageAccuracy >= 80 ? '✓ Complete' : `${80 - progressData.averageAccuracy}% to go`}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Score 90%+ on a single test</span>
              <Badge variant="outline" className="text-xs">
                {progressData.bestAccuracy >= 90 ? '✓ Complete' : `${90 - progressData.bestAccuracy}% to go`}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Encouragement */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="text-center space-y-2">
            <h4 className="font-semibold text-blue-900">Keep Going! 🎸</h4>
            <p className="text-sm text-blue-800">
              {progressData.averageAccuracy >= 80 && "Excellent progress! You're becoming a fretboard master."}
              {progressData.averageAccuracy >= 70 && progressData.averageAccuracy < 80 && "Great work! Your fretboard knowledge is really improving."}
              {progressData.averageAccuracy >= 60 && progressData.averageAccuracy < 70 && "Good progress! Keep practicing to build your confidence."}
              {progressData.averageAccuracy < 60 && "Every expert started as a beginner. Keep practicing!"}
            </p>
          </div>
        </Card>
      </CardContent>
    </Card>
  );
};

export default FretboardProgress;